import { HubConnectionBuilder } from '@aspnet/signalr';
import { SignalRFileTransfer } from './signalr-file-transfer';
// /**
//  * A STUN server is used to get an external network address. TURN servers are used to relay traffic
//  * if direct (peer to peer) connection fails.
//  */
// const iceServers: RTCIceServer[] = [
//     { urls: ["stun:stun2.1.google.com:19302"] }
// ];
/**
 * Handles WebRTC signaling messages on the client side.
 */
export class SignalRSignalingChannel {
    /**
     *
     * @param signalingUrl The URL of the signaling hub.
     * @param iceServers A collection of ICE servers that can be used for WebRTC.
     * @param groupName The name of the SignalR group. The group name equals the preview session ID.
     * @param isInitiator Indicates whether this client is the initiator of the session.
     * @param canUseWebRTC Indicates whether WebRTC can be used for transferring preview data. Even though the device supports
     * WebRTC, data channel support may still be insuficcient, such as on iOS11/12, see https://github.com/webrtc/samples/issues/1123.
     * @param createPeerConnectionCallback
     */
    constructor(signalingUrl, iceServers, groupName, isInitiator, previewScope, instanceId, canUseWebRTC, createPeerConnectionCallback) {
        this.iceServers = iceServers;
        this.groupName = groupName;
        this.isInitiator = isInitiator;
        this.previewScope = previewScope;
        this.instanceId = instanceId;
        this.canUseWebRTC = canUseWebRTC;
        this.createPeerConnectionCallback = createPeerConnectionCallback;
        this.isConnected = false;
        this.joinGroupPromise = null;
        this.onPeerJoined = null;
        this.onPeerConnectionCreated = null;
        this.onPeerLeave = null;
        this.onConnected = null;
        this.peerConnections = {};
        /**
         * The unique client ID assigned by the signaling server upon login.
         */
        this.clientId = null;
        let connection = new HubConnectionBuilder()
            .withUrl(signalingUrl)
            // .configureLogging(SignalRLogLevel.Trace)
            .build();
        this.connection = connection;
        connection.on('ReceiveServerSignalingMessage', data => {
            this.handleSignalingMessage(data);
        });
        console.log(`Signaling: starting connection..`);
        connection
            .start()
            .then(() => {
            this.isConnected = true;
            // console.log(`Signaling: connected.`);
            if (this.onConnected)
                this.onConnected();
        })
            .catch(reason => {
            console.error("Signaling error", reason);
        });
        connection
            .onclose(async (e) => {
            this.isConnected = false;
        });
        // Note: do not register to onbeforeunload events, because the UI allows users to cancel unloading there
        window.addEventListener('unload', () => {
            this.leaveGroup();
        });
    }
    createFileTransfer() {
        return new SignalRFileTransfer(this.connection, this.clientId);
    }
    // #region signaling message handling
    sendSignalingMessage(message) {
        if (!this.isConnected) {
            if (message.type === 5 /* SignalingMessageType.LeaveGroup */)
                return; // don't throw errors if leaving (probably closing the browser)
            throw `Signaling: unable to send signaling message. The client is not connected.`;
        }
        this.connection
            .invoke('ReceiveClientSignalingMessage', message)
            .catch(reason => console.error("SignalR error", reason));
    }
    handleSignalingMessage(message) {
        if (!message.type)
            return console.warn(`Signaling: received a signaling message that has no message type:`, message);
        switch (message.type) {
            case 3 /* SignalingMessageType.JoinGroupResponse */:
                this.handleJoinGroupResponseMessage(message);
                break;
            case 4 /* SignalingMessageType.JoinedGroup */:
                this.handleJoinedGroupMessage(message);
                break;
            case 10 /* SignalingMessageType.PeerConnectionRequest */:
                this.handlePeerConnectionRequestMessage(message);
                break;
            case 6 /* SignalingMessageType.LeftGroup */:
                this.handleLeftGroupMessage(message);
                break;
            case 1 /* SignalingMessageType.Log */:
                this.handleLogMessage(message);
                break;
            case 8 /* SignalingMessageType.RTCOffer */:
                this.handleOfferMessage(message);
                break;
            case 7 /* SignalingMessageType.RTCIceCandidate */:
                this.handleIceCandidateMessage(message);
                break;
            case 9 /* SignalingMessageType.RTCAnswer */:
                this.handleAnswerMessage(message);
                break;
            case 11 /* SignalingMessageType.PreviewFallback */:
                this.handlePeerMessage(message);
                break;
            default:
                console.warn(`Signaling: received unsupported signaling message with type '${message.type}'.`);
                break;
        }
    }
    handleJoinedGroupMessage(message) {
        console.log(`Signaling: peer ${message.clientId} has joined.`);
        if (this.onPeerJoined) {
            this.onPeerJoined(message);
        }
    }
    /**
     * Called when a user logged into (wether successful or not) the signaling server.
     */
    handleJoinGroupResponseMessage(response) {
        // This is a response to join(). Resolve it.
        if (!response.success) {
            console.warn(`Signaling: joining group '${response.groupName}' has failed':`, response.reason); // don't reject the promise, this is no error
        }
        // Remember our client ID
        this.clientId = response.clientId;
        if (this.joinGroupPromise) {
            // resolve the call to joinGroup()
            this.joinGroupPromise.resolve(response);
            this.joinGroupPromise = null;
        }
    }
    handleLeftGroupMessage(message) {
        console.log(`Signaling: peer '${message.clientId}' has left.`);
        this.disposePeerConnection(message.clientId);
        if (this.onPeerLeave)
            this.onPeerLeave(message.clientId);
    }
    /**
     * Handles log messages received from the signaling server.
     */
    handleLogMessage(message) {
        switch (message.level) {
            case 1 /* LogLevel.Info */:
                console.error(`Signaling server:`, message.message);
                break;
            case 2 /* LogLevel.Warning */:
                console.warn(`Signaling server:`, message.message);
                break;
            case 3 /* LogLevel.Error */:
                console.error(`Signaling server:`, message.message);
                break;
            default:
                console.log(`Signaling server:`, message.message);
                break;
        }
    }
    sendPeerMessage(message) {
        this.sendSignalingMessage(message);
    }
    handlePeerMessage(message) {
        const peer = this.peerConnections[message.fromClientId];
        if (!peer)
            throw `Signaling: unable to handle peer message. There is no peer with client ID '${message.fromClientId}'.`;
        //  console.log(`Received ICE candidate: `, message.candidate);
        peer.handlePeerMessage(message);
    }
    /**
     * Received when a peer sends an ICE candidate to us.
     */
    handleIceCandidateMessage(message) {
        const peer = this.peerConnections[message.fromClientId];
        if (!peer)
            throw `Signaling: unable to handle ICE candidate. There is no peer with client ID '${message.fromClientId}'.`;
        //  console.log(`Received ICE candidate: `, message.candidate);
        peer.handleIceCandidateMessage(message);
    }
    /**
     * Called when a the initiator sends us an offer.
     */
    handleOfferMessage(offer) {
        const peer = this.peerConnections[offer.fromClientId];
        if (!peer)
            throw `Signaling: unable to handle offer. There is no peer with client ID '${offer.fromClientId}'. Own client ID: ${this.clientId}.`;
        peer.handleOfferMessage(offer);
    }
    handleAnswerMessage(answer) {
        const peer = this.peerConnections[answer.fromClientId];
        if (!peer)
            throw `Signaling: unable to handle answer. There is no peer with client ID '${answer.fromClientId}'.`;
        peer.handleAnswerMessage(answer);
    }
    // #endregion signaling message handling
    /**
     * Logs into the signaling server and creates a peer connection when done.
     * @param groupName The name of the group to join if the
     */
    joinGroup() {
        // TODO: should we make some timeout mechanism?
        const promise = new Promise((resolve, reject) => {
            this.joinGroupPromise = { resolve, reject };
        });
        const request = {
            type: 2 /* SignalingMessageType.JoinGroupRequest */,
            groupName: this.groupName,
            isHost: this.isInitiator,
            scope: this.previewScope,
            instanceId: this.instanceId || undefined,
            canUseWebRTC: this.canUseWebRTC
        };
        this.sendSignalingMessage(request);
        return promise;
    }
    /**
     * Disconnects from the current session.
     */
    leaveGroup() {
        if (!this.clientId)
            return;
        // Dispose all peer connections
        const keys = Object.keys(this.peerConnections);
        for (const clientId in keys) {
            this.disposePeerConnection(clientId);
        }
        const msg = { type: 5 /* SignalingMessageType.LeaveGroup */, groupName: this.groupName };
        this.sendSignalingMessage(msg);
    }
    /**
     * Disconnects from the current session and connects to a new one.
     */
    changeGroup(groupName) {
        this.leaveGroup();
        this.groupName = groupName;
        return this.joinGroup();
    }
    disposePeerConnection(clientId) {
        const peer = this.peerConnections[clientId];
        if (!peer)
            return;
        console.log(`Signaling: disposing connection with '${clientId}'.`);
        peer.dispose();
        delete this.peerConnections[clientId];
    }
    connectToPeer(peerClientId, peerScope, peerInstanceId, peerCanUseWebRTC) {
        console.log(`Signaling: creating peer connection with ${peerClientId} (scope ${peerScope}, instanceId ${peerInstanceId}) (initiating).`);
        if (peerInstanceId != null && this.instanceId != null && peerInstanceId !== this.instanceId) {
            return console.log(`No connecting to peer because instanceId ${peerInstanceId} does not match own instanceId ${this.instanceId}.`);
        }
        const peerConnection = this.createPeerConnection(peerClientId, peerScope, peerCanUseWebRTC);
        // Send a message to the peer, asking to also set up a connection with us.
        // The peer will handle this in handlePeerConnectionRequestMessage (below)
        const request = {
            type: 10 /* SignalingMessageType.PeerConnectionRequest */,
            fromClientId: this.clientId,
            toClientId: peerClientId,
            // Pass our own previewScope, instanceId and whether WE can use WebRTC
            scope: this.previewScope,
            instanceId: this.instanceId || undefined,
            canUseWebRTC: true
        };
        this.sendSignalingMessage(request);
        // TODO: we could also wait until the client has ensured a connection with us in handlePeerConnectionRequestMessage, but is this needed?
        if (this.onPeerConnectionCreated)
            this.onPeerConnectionCreated(peerConnection, true);
    }
    handlePeerConnectionRequestMessage(message) {
        if (message.instanceId != null && this.instanceId != null && message.instanceId !== this.instanceId) {
            return console.log(`Ignoring peer connection request because instanceId '${message.instanceId}' does not match own instanceId ${this.instanceId}.`);
        }
        console.log(`Signaling: creating peer connection with ${message.fromClientId} (requested by peer with scope: ${message.scope}).`);
        const peerConnection = this.createPeerConnection(message.fromClientId, message.scope, message.canUseWebRTC);
        if (this.onPeerConnectionCreated) {
            this.onPeerConnectionCreated(peerConnection, false);
        }
    }
    createPeerConnection(toClientId, scope, peerCanUseWebRTC) {
        if (!this.isConnected)
            throw `Signaling: unable to create peer connection. The signaling client is not connected.`;
        let peerConnection;
        if (peerCanUseWebRTC) {
            const configuration = { iceServers: this.iceServers };
            peerConnection = new RTCPeerConnection(configuration);
        }
        const connectionHandler = this.createPeerConnectionCallback(this, peerConnection, scope, this.clientId, toClientId);
        // Make sure that the current handler, if any, is disposed.
        this.disposePeerConnection(toClientId);
        this.peerConnections[toClientId] = connectionHandler;
        return connectionHandler;
    }
    getPeers() {
        return this.peerConnections;
    }
}
//# sourceMappingURL=signalr-signaling-channel.js.map